import { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from '@/routing/navigation'
import { useOutletContext } from 'react-router-dom'
import { useAuth } from '@/lib/config/auth-provider'
import {
  computeShareTitle,
  type SharedShellOutletContext,
} from '@/spa/layouts/SharedAppShellLayout'
import { setReferralCookies, getReferralCookies } from '@/lib/utils/referral-cookie'
import { track, AnalyticsEvents } from '@/lib/services/analytics'
import dynamic from '@/routing/dynamic'
import type {
  PublicReportIntentReplay,
  ReportLandingPageProps,
} from '@/features/studies/report/page/components/ReportLandingPage'
import type { ReportWaitingPageProps } from '@/features/studies/report/page/components/ReportWaitingPage'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { publicApi } from '@/lib/api'
import { resolveLegacyFairgenLogoUrl } from '@/lib/theme/fairgen-default-brand'
import {
  ensureShareGuestIntent,
  getGuestSessionToken,
  hasValidGuestSessionToken,
} from '@/lib/utils/share-guest-intent'
import { setChatSocketAuthToken } from '@/lib/services/chat-socket'
import {
  onPublicShareRevision,
  subscribeToPublicShare,
  unsubscribeFromPublicShare,
} from '@/lib/services/public-socket'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'
import { onGuestSessionExpired } from '@/lib/utils/guest-session-events'
import { Card, CardContent } from '@/components/ui/card'
import { Loader2, AlertCircle, Lock } from 'lucide-react'
import { EmailGateForm, type EmailGateVerifiedResult } from './components/EmailGateForm'
import { mergeProfilesWithResponseImages } from '@/features/studies/report/audienceProfiles'
import { interpretationStillBuilding } from '@/features/studies/report/readiness'
import type { SharedReportEnvelope } from '@/lib/api/client'
import { servedDeckStatus } from '@/features/studies/report/reportViewState'
import { DeckStatus } from '@digital-twins/shared-types/reporting'
import type { Deck } from '@digital-twins/deck-ui/slides/contracts'
import type { ReportReadiness } from '@/types/routes/test-results'
import type { CanonicalAnalysisOutput } from '@digital-twins/shared-types'
import type { PublicStoryline } from '@digital-twins/shared-types/reporting'

const ReportLandingPage = dynamic<ReportLandingPageProps>(
  () =>
    import('@/features/studies/report/page/components/ReportLandingPage').then(
      (mod) => ({ default: mod.ReportLandingPage }),
    ),
  {
    loading: () => (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    ),
  },
)

const ReportWaitingPage = dynamic<ReportWaitingPageProps>(
  () =>
    import('@/features/studies/report/page/components/ReportWaitingPage').then(
      (mod) => ({ default: mod.ReportWaitingPage }),
    ),
  {
    loading: () => (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    ),
  },
)

/** The share payload; `SharedReportEnvelope` is checked at the boundary. */
interface PublicReportData extends SharedReportEnvelope {
  experiment: {
    id: string
    name: string
    status: string
    numRespondents: number
    createdAt: string
    completedAt: string
    deckStatus?: DeckStatus
    deckEnabled?: boolean
    twinChatEnabled?: boolean
    populationId?: string
    goalId?: string | null
    /** The current analysis has its scale tab book (SS-6). */
    hasScaleTables?: boolean
    studyWorkbookStatus?: 'legacy' | 'building' | 'ready' | 'failed'
  }
  survey: any
  results: any[]
  metadata: any
  executiveSummary?: any
  canonicalAnalysisJson?: CanonicalAnalysisOutput | null
  /** The reader storyline (generated `PublicStoryline`), or null. */
  storyline?: PublicStoryline | null
  deckJson?: any
  audienceProfilesJson?: any[]
  coverImageUrl?: string
  reportHeadline?: string
  readiness: ReportReadiness
  brandLogoUrl?: string | null
  brandLogoUrlOnDark?: string | null
  guestChatEnabled?: boolean
}

/** `unavailable`: the report arrived in a shape the page cannot trust. */
type AccessPhase = 'checking' | 'email_gate' | 'loading' | 'ready' | 'unavailable' | 'error'
type AccessMode = 'read_only' | 'email_required' | null

export default function PublicReportPage() {
  const { t } = useTranslation('app')
  const params = useParams()
  const token = params.token as string
  const searchParams = useSearchParams()
  const { isLoading: authLoading } = useAuth()
  const outletContext = useOutletContext<SharedShellOutletContext | undefined>()
  const setStudyName = outletContext?.setStudyName
  const branding = outletContext?.branding ?? null
  const whiteLabel = outletContext?.whiteLabel === true
  const [phase, setPhase] = useState<AccessPhase>('checking')
  const [accessMode, setAccessMode] = useState<AccessMode>(null)
  const [report, setReport] = useState<PublicReportData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [brandLogo, setBrandLogo] = useState<{ url: string | null; urlOnDark: string | null }>({ url: null, urlOnDark: null })
  const [isIntentGateOpen, setIsIntentGateOpen] = useState(false)
  const [verifiedShareToken, setVerifiedShareToken] = useState<string | null>(null)
  const [orgAccessGranted, setOrgAccessGranted] = useState(false)
  const [pendingIntent, setPendingIntent] = useState<PublicReportIntentReplay | null>(null)
  const [intentReplay, setIntentReplay] = useState<PublicReportIntentReplay | null>(null)
  const coverRefreshStartedAt = useRef<number | null>(null)
  const intentSequence = useRef(0)

  const reportName = report?.experiment?.name ?? ''
  const ownerOrgName = branding?.orgName ?? ''
  const helmetTitle = computeShareTitle(whiteLabel, reportName, ownerOrgName)

  useEffect(() => {
    if (!setStudyName) return
    setStudyName(reportName)
  }, [setStudyName, reportName])

  useEffect(() => {
    const refCode = searchParams.get('ref')
    if (refCode && !getReferralCookies().referralCode) {
      // Only write if no cookie exists yet — preserves 'referral_link' source
      // set by SpaRootLayoutEffects when the visitor arrived via a plain referral link.
      setReferralCookies(refCode, 'study_share')
    }
    track(AnalyticsEvents.SHARED_STUDY_VIEWED, { token })
  }, [token, searchParams])

  // Step 1: Check access mode + store org logo locally.
  // Step 2: "Anyone with the link" loads read-only immediately. Invite-based
  // shares keep the mandatory OTP gate unless this device already has a valid
  // guest_session JWT scoped to the share token.
  useEffect(() => {
    if (authLoading) return
    let cancelled = false
    async function checkAccess() {
      try {
        const result = await publicApi.getAccessMode(token)
        if (cancelled) return

        setBrandLogo({
          url: resolveLegacyFairgenLogoUrl(result.brandLogoUrl),
          urlOnDark: resolveLegacyFairgenLogoUrl(result.brandLogoUrlOnDark),
        })

        if (result.mode === 'unavailable') {
          setAccessMode(null)
          setError('Report not found or is no longer public')
          setPhase('error')
          return
        }

        setAccessMode(result.mode)
        ensureShareGuestIntent(`/share/${encodeURIComponent(token)}`)

        if (result.mode === 'read_only' || hasValidGuestSessionToken(token)) {
          setPhase('loading')
        } else {
          setPhase('email_gate')
        }
      } catch {
        if (!cancelled) {
          setError('Failed to check report access')
          setPhase('error')
        }
      }
    }
    checkAccess()
    return () => { cancelled = true }
  }, [token, authLoading])

  // A lapsed OTP session used to surface as a dead 401 inside whichever tab
  // the guest was using. Reopen the gate instead, and replay that tab's action
  // once they verify again.
  useEffect(() => {
    return onGuestSessionExpired((tab) => {
      publicApi.clearGuestToken()
      setVerifiedShareToken(null)
      intentSequence.current += 1
      setPendingIntent({ id: intentSequence.current, tab })
      setIsIntentGateOpen(true)
    })
  }, [])

  // The chat socket authenticates from the fairgen-jwt cookie, which share
  // routes deliberately clear. Hand it the guest JWT instead so focus-group
  // rooms receive live turn events, and drop it when leaving the share page.
  useEffect(() => {
    setChatSocketAuthToken(getGuestSessionToken(token))
    return () => setChatSocketAuthToken(null)
  }, [token, verifiedShareToken, phase])

  // Step 2: Load report data (once access is granted)
  const loadReport = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) {
        setError(null)
      }
      const parsed = await publicApi.getReport<PublicReportData>(token)
      if (!parsed.ok) {
        // A silent refresh keeps the last report it could read.
        if (!silent) {
          setPhase('unavailable')
        }
        return
      }
      const data = parsed.data
      setReport(data)
      setPhase('ready')
      setBrandLogo({
        url: resolveLegacyFairgenLogoUrl(data.brandLogoUrl),
        urlOnDark: resolveLegacyFairgenLogoUrl(data.brandLogoUrlOnDark),
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : ''
      if (message.includes('Email verification required')) {
        setPhase('email_gate')
        return
      }
      if (!silent) {
        setError(message || 'Failed to load report')
        setPhase('error')
      }
    }
  }, [token])

  useEffect(() => {
    if (phase === 'loading') {
      loadReport()
    }
  }, [phase, loadReport])

  const awaitingShareRefresh =
    Boolean(report) &&
    phase === 'ready' &&
    report!.readiness.overallStatus !== 'failed' &&
    (() => {
      const readiness = report!.readiness
      const shouldPollForLateCover =
        readiness.isReady &&
        !report!.coverImageUrl &&
        (!coverRefreshStartedAt.current ||
          Date.now() - coverRefreshStartedAt.current < 3 * 60 * 1000)
      const shouldPollForDeckInFlight =
        readiness.isReady && readiness.steps.deck.status === 'processing'
      return (
        !readiness.isReady ||
        shouldPollForLateCover ||
        shouldPollForDeckInFlight ||
        interpretationStillBuilding(readiness.availability)
      )
    })()

  useRealtimeRepair({
    active: awaitingShareRefresh,
    onRepair: (reason) => {
      incrementPollWonRepair('PUBLIC', reason)
      void loadReport({ silent: true })
    },
  })

  useEffect(() => {
    if (!token || !awaitingShareRefresh) {
      return
    }
    subscribeToPublicShare(token)
    const offRevision = onPublicShareRevision(token, () => {
      void loadReport({ silent: true })
    })
    return () => {
      offRevision()
      unsubscribeFromPublicShare(token)
    }
  }, [awaitingShareRefresh, loadReport, token])

  const handleEmailVerified = (result?: EmailGateVerifiedResult) => {
    setVerifiedShareToken(token)
    if (result?.organizationAccessGranted) {
      setOrgAccessGranted(true)
    }
    if (isIntentGateOpen) {
      setIsIntentGateOpen(false)
      if (pendingIntent) {
        setIntentReplay(pendingIntent)
      }
      setPendingIntent(null)
      return
    }
    setPhase('loading')
  }

  const handlePublicInteractionIntent = useCallback(
    (tab: PublicReportIntentReplay['tab']) => {
      if (accessMode !== 'read_only') {
        return true
      }
      if (verifiedShareToken === token || hasValidGuestSessionToken(token)) {
        return true
      }

      const intent = {
        id: intentSequence.current + 1,
        tab,
      }
      intentSequence.current = intent.id
      setPendingIntent(intent)
      setIsIntentGateOpen(true)
      return false
    },
    [accessMode, token, verifiedShareToken],
  )

  // --- Render phases ---

  if (phase === 'checking') {
    return (
      <div data-testid="share-checking" className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
          <Text size="sm" color="muted">Checking access...</Text>
        </div>
      </div>
    )
  }

  if (phase === 'email_gate') {
    return (
      <div data-testid="share-email-gate" className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-background/60">
        <EmailGateForm shareToken={token} onVerified={handleEmailVerified} />
      </div>
    )
  }

  if (phase === 'loading') {
    return (
      <div data-testid="share-loading" className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
          <Text size="sm" color="muted">Loading report...</Text>
        </div>
      </div>
    )
  }

  if (phase === 'unavailable') {
    return (
      <div data-testid="share-unavailable" className="flex min-h-[60vh] items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <Heading level={2}>{t('studyReport.shareUnavailable.title')}</Heading>
            <Text size="sm" color="muted" className="max-w-xs">
              {t('studyReport.shareUnavailable.body')}
            </Text>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (phase === 'error' || error) {
    return (
      <div data-testid="share-error" className="flex min-h-[60vh] items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              {error?.includes('not found') || error?.includes('no longer public') ? (
                <Lock className="h-7 w-7 text-destructive" />
              ) : (
                <AlertCircle className="h-7 w-7 text-destructive" />
              )}
            </div>
            <Heading level={2}>
              {error?.includes('not found') || error?.includes('no longer public')
                ? 'Report Not Available'
                : 'Unable to Load Report'}
            </Heading>
            <Text size="sm" color="muted" className="max-w-xs">
              {error?.includes('not found') || error?.includes('no longer public')
                ? 'This report link may have expired or the owner has disabled public sharing.'
                : error}
            </Text>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!report) {
    return null
  }

  const deck: Deck | null = report.deckJson ? {
    ...report.deckJson,
    experimentId: report.experiment.id,
  } : null
  const readiness = report.readiness
  const hasReportData = Array.isArray(report.results)

  const rawProfiles = report.audienceProfilesJson
  const profilesArray = Array.isArray(rawProfiles)
    ? rawProfiles
    : Array.isArray((rawProfiles as any)?.profiles)
      ? (rawProfiles as any).profiles
      : []
  const audienceProfiles = mergeProfilesWithResponseImages(profilesArray, report.results)

  return (
    <div className="animate-in fade-in duration-500">
      {helmetTitle && (
        <Helmet>
          <title>{helmetTitle}</title>
        </Helmet>
      )}
      {orgAccessGranted && (
        <div
          data-testid="share-org-access-granted"
          className="flex flex-wrap items-center justify-center gap-2 border-b border-border bg-muted/50 px-4 py-2.5 text-center"
        >
          <Text size="sm">
            This study is now available to your organization in the Studies tab.
          </Text>
          <a href="/studies" className="text-sm font-medium underline underline-offset-4">
            Open Studies
          </a>
        </div>
      )}
      {readiness.isReady && report.survey && hasReportData ? (
        <ReportLandingPage
          studyId={report.experiment.id}
          studyName={report.experiment.name}
          createdAt={report.experiment.completedAt}
          survey={report.survey}
          responses={report.results}
          executiveSummary={report.executiveSummary}
          canonicalAnalysis={report.canonicalAnalysisJson}
          storyline={report.storyline}
          audienceProfiles={audienceProfiles}
          coverImageUrl={report.coverImageUrl}
          reportHeadline={report.reportHeadline}
          deck={deck}
          deckStatus={servedDeckStatus({
            study: report.experiment.deckStatus,
            deckStep: readiness.steps.deck.status,
            live: null,
          })}
          deckEnabled={report.experiment.deckEnabled}
          twinChatEnabled={
            report.experiment.twinChatEnabled === true &&
            branding?.chatRoomsEnabled !== false
          }
          hasScaleTables={report.experiment.hasScaleTables === true}
          studyWorkbookStatus={report.experiment.studyWorkbookStatus}
          guestChatEnabled={report.guestChatEnabled === true}
          populationId={report.experiment.populationId}
          goalId={report.experiment.goalId}
          availability={readiness.availability}
          isPublicView={true}
          shareToken={token}
          brandLogoUrl={brandLogo.url}
          brandLogoUrlOnDark={brandLogo.urlOnDark}
          onPublicInteractionIntent={handlePublicInteractionIntent}
          publicIntentReplay={intentReplay}
          resultsMetadata={report.metadata}
          respondentsCompleted={Array.isArray(report.results) ? report.results.length : undefined}
          numRespondents={report.experiment.numRespondents}
          studyStatus={report.experiment.status}
        />
      ) : !hasReportData ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Text color="muted">Report data is not available.</Text>
          </CardContent>
        </Card>
      ) : (
        <ReportWaitingPage
          studyName={report.experiment.name}
          readiness={readiness}
          respondentsCompleted={report.results?.length}
          numRespondents={report.experiment.numRespondents}
          progressPercent={readiness.steps.responses.status === 'ready' ? 100 : undefined}
          showBackLink={false}
        />
      )}
      {isIntentGateOpen && (
        <div data-testid="share-email-gate" className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-background/60">
          <EmailGateForm shareToken={token} onVerified={handleEmailVerified} />
        </div>
      )}
    </div>
  )
}
